import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import axiosInstance from "../../../services/AxiosInstance";

export const STATUS = {
  needs_approval: { label: "Needs approval", color: "#E0A21B" },
  scheduled: { label: "Scheduled", color: "#3B82C4" },
  sent: { label: "Sent", color: "#3FA876" },
  failed: { label: "Failed", color: "#E5484D" },
  planned: { label: "Planned", color: "#9aa59d" },
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["M", "T", "W", "T", "F", "S", "S"];

const YearView = ({ year, events }) => {
  const days = useMemo(() => {
    const m = {};
    events.forEach((e) => { const d = new Date(e.at); if (d.getFullYear() === year) m[`${d.getMonth()}-${d.getDate()}`] = (m[`${d.getMonth()}-${d.getDate()}`] || 0) + 1; });
    return m;
  }, [events, year]);
  const today = new Date();
  return (
    <div className="rf-year">
      {MONTHS.map((name, mi) => {
        const first = (new Date(year, mi, 1).getDay() + 6) % 7;
        const n = new Date(year, mi + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < first; i++) cells.push(<span key={`b${i}`} />);
        for (let d = 1; d <= n; d++) {
          const isToday = today.getFullYear() === year && today.getMonth() === mi && today.getDate() === d;
          cells.push(<span key={d} className={`${days[`${mi}-${d}`] ? "has" : ""} ${isToday ? "today" : ""}`} title={days[`${mi}-${d}`] ? `${days[`${mi}-${d}`]} post(s)` : ""}>{d}</span>);
        }
        return (
          <div className="rf-mini" key={name}>
            <h6>{name}</h6>
            <div className="g">{DOW.map((x, i) => <b key={i} className="rf-meta">{x}</b>)}{cells}</div>
          </div>
        );
      })}
    </div>
  );
};

const ChannelCalendar = ({ channels, withFlows, initialChannelId }) => {
  const [mode, setMode] = useState("month");
  const [channelId, setChannelId] = useState(initialChannelId || "");
  const [year, setYear] = useState(new Date().getFullYear());
  const [range, setRange] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const calRef = useRef(null);
  const tokRef = useRef(0);

  const load = useCallback(async () => {
    const r = mode === "year" ? { from: new Date(year, 0, 1), to: new Date(year + 1, 0, 1) } : range;
    if (!r) return;
    const tok = ++tokRef.current;
    setLoading(true); setErr("");
    const params = { from: r.from.toISOString(), to: r.to.toISOString() };
    if (channelId) params.channelId = channelId;
    try {
      let res;
      try { res = await axiosInstance.get(withFlows ? "flows/calendar" : "channels/calendar", { params }); }
      catch (e) { if (!withFlows) throw e; res = await axiosInstance.get("channels/calendar", { params }); }
      if (tok !== tokRef.current) return;
      setEvents(res.data.data || []);
    } catch (e) { if (tok !== tokRef.current) return; setErr(e?.response?.data?.message || e.message); }
    setLoading(false);
  }, [mode, year, range, channelId, withFlows]);

  useEffect(() => { load(); }, [load]);

  const fcEvents = events.map((e) => ({
    id: e.id, start: e.at, title: `${e.channelName ? e.channelName + ": " : ""}${e.text || "(no text)"}`,
    backgroundColor: (STATUS[e.status] || STATUS.planned).color, borderColor: (STATUS[e.status] || STATUS.planned).color,
    classNames: [`ev-${e.status}`],
  }));

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
        <div className="rf-seg">
          <button className={mode === "month" ? "on" : ""} onClick={() => setMode("month")}>Month</button>
          <button className={mode === "year" ? "on" : ""} onClick={() => setMode("year")}>Year</button>
        </div>
        {mode === "year" && (
          <div className="rf-seg">
            <button onClick={() => setYear(year - 1)}>‹</button><button className="on">{year}</button><button onClick={() => setYear(year + 1)}>›</button>
          </div>
        )}
        <select className="form-control" style={{ maxWidth: 220 }} value={channelId} onChange={(e) => setChannelId(e.target.value)}>
          <option value="">All channels</option>
          {channels.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        {loading && <Spinner animation="border" size="sm" variant="success" />}
      </div>
      <div className="rf-legend">
        {Object.entries(STATUS).map(([k, v]) => (k === "needs_approval" || k === "planned") && !withFlows ? null : <span key={k}><i style={{ background: v.color, ...(k === "planned" ? { background: "transparent", border: "1.5px dashed #9aa59d" } : {}) }} />{v.label}</span>)}
      </div>
      {err && <div className="rf-alert red">{err}</div>}
      <div className="rf-card">
        {mode === "year" ? <YearView year={year} events={events} /> : (
          <FullCalendar
            ref={calRef}
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            firstDay={1}
            height="auto"
            dayMaxEvents={3}
            events={fcEvents}
            headerToolbar={{ left: "prev,next today", center: "title", right: "" }}
            datesSet={(a) => setRange((p) => (p && +p.from === +a.start && +p.to === +a.end ? p : { from: a.start, to: a.end }))}
            eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          />
        )}
      </div>
    </div>
  );
};

export default ChannelCalendar;
